import {Component, Inject, Input, OnChanges, OnInit, PLATFORM_ID, SimpleChanges} from '@angular/core';
import {IPicture} from '@api/models/image/picture';
import {IMAGE_RESOLUTION} from '@api/models/image/image-resolution';
import {IVideo} from '@api/models/video/video';
import {ImageService} from '@utils/services/image/image.service';
import {ImageSubType} from '@api/models/image/image-type';
import {IPlayer} from '@api/models/player/player';
import {MatchPlayersService} from '../../services/match-players/match-players.service';
import {MatchService} from '../../services/match/match.service';
import {isPlatformBrowser} from '@angular/common';
import {TickerItemType} from '@api/models/ticker/ticker-item-type';
import {IMatchTickerItem} from '../../models/match-ticker-item';
import {ITickerItemData, TickerItem} from '@api/models/ticker/ticker-item';
import {ColorScheme} from '@lib/models/color-scheme/color-scheme';
import {SubscriptionContentType} from '@lib/models/subscription/subscription-content-type';
import {SubscriptionIdType} from '@lib/models/subscription/subscription-id-type';
import {SubscriptionTrackingInfo} from '@lib/models/subscription/subscription-tracking-info';

@Component({
  selector: 'lib-match-ticker-item',
  templateUrl: './match-ticker-item.component.html',
  styleUrls: ['./match-ticker-item.component.scss']
})
export class MatchTickerItemComponent implements OnInit, OnChanges {

  @Input() public item: IMatchTickerItem;
  @Input() public data: ITickerItemData;
  @Input() public pictures: IPicture[];
  @Input() public videos: IVideo[];
  @Input() public teamPictures: IPicture[];
  @Input() public colorScheme = ColorScheme.Default;
  @Input() public isFirst = false;
  @Input() public isLast = false;
  public isComment = false;
  public isGoal = false;
  public isRedCard = false;
  public isSubstitution = false;
  public players: { inPlayer: IPlayer, outPlayer: IPlayer };
  public playerImage: string;
  public hasVideo = false;
  public mainImages: IPicture[];
  public tickerUrl: string;
  public imageRes = IMAGE_RESOLUTION.LOW;
  public imageType = ImageSubType.LOGO;
  public trackingInfo: SubscriptionTrackingInfo = null;

  constructor(private matchPlayerService: MatchPlayersService,
              private matchService: MatchService,
              @Inject(PLATFORM_ID) private platformId: any) {
  }

  ngOnInit(): void {
    this.tickerUrl = this.matchService.tickerUrl;
    this.isGoal = TickerItem.isType(this.item, TickerItemType.Goal);
    this.isRedCard = TickerItem.isType(this.item, TickerItemType.RedCard);
    this.isSubstitution = TickerItem.isType(this.item, TickerItemType.Substitution);
    this.isComment = TickerItem.isType(this.item, TickerItemType.Comment);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.data) {
      this.loadPlayers();
    }
    if (changes.videos) {
      this.handleVideoChanges();
    }
    if (changes.pictures) {
      this.handlePictureChanges();
    }
    this.setTrackingInfo();
  }

  private setTrackingInfo() {
    if (!this.hasVideo) {
      return;
    }
    this.trackingInfo = {
      contentType: SubscriptionContentType.ACTIVITY,
      idType: SubscriptionIdType.ACTIVITY_ID,
      id: this.item._id,
    }
  }

  private handlePictureChanges(): void {
    if (!this.pictures?.length && !this.videos?.length) {
      this.mainImages = [];
      this.hasVideo = false;
      return;
    }
    if (!this.pictures?.length) {
      return;
    }
    this.mainImages = this.pictures.filter(picture => picture.subtype === ImageSubType.IMAGE);
    this.playerImage = this.getPlayerImage(this.pictures);
  }

  private handleVideoChanges(): void {
    if (!this.pictures?.length && !this.videos?.length) {
      this.hasVideo = false;
      return;
    }
    if (!this.videos?.length) {
      return;
    }
    const video = this.videos[0];
    if (!video.preview) {
      this.hasVideo = false;
      return;
    }
    const image = ImageService.findPictureUrlBySubType(
      [video.preview], ImageSubType.IMAGE, IMAGE_RESOLUTION.HIGH
    );
    if (image) {
      this.hasVideo = true;
    }
  }

  private async loadPlayers(): Promise<void> {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    this.players = {inPlayer: null, outPlayer: null};
    if (this.item.data?.in_playerid) {
      this.players.inPlayer = await this.matchPlayerService.getPlayer(this.item.data.in_playerid);
      this.players.inPlayer.imgUrl = this.getPlayerImage(this.players.inPlayer.pictures);
    }
    if (this.item.data?.out_playerid) {
      this.players.outPlayer = await this.matchPlayerService.getPlayer(this.item.data.out_playerid);
      this.players.outPlayer.imgUrl = this.getPlayerImage(this.players.outPlayer.pictures);
    }
  }

  private getPlayerImage(playerPictures: IPicture[]): string | null {
    let playerImage = ImageService.findPictureUrlBySubType(
      playerPictures, ImageSubType.SPONSOR, IMAGE_RESOLUTION.LOW
    );
    if (!playerImage) {
      playerImage = ImageService.findPictureUrlBySubType(
        playerPictures, ImageSubType.FACE, IMAGE_RESOLUTION.LOW
      );
    }
    if (playerImage && !playerImage.includes('placeholder')) {
      return playerImage;
    }
    return null;
  }

  public get lineupPlayerName(): string {
    const player = this.matchService.getLineupPlayerById(this.item.playerid);
    if (!player) {
      return null;
    }
    return player.playername || (player.firstname + ' ' + player.lastname);
  }
}
